import { useFormik } from 'formik';
import {
  ChangeEvent, useEffect, useId, useState,
} from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import usePostDriver from '../../../../API/hooks/drivers/usePostDriver';
import { PostDriverRequest } from '../../../../API/types/Drivers';
import { IconKeys } from '../../../../components/common/Icon/IconKeys';
import Input from '../../../../components/common/Input/Input';
import { Button } from '../../../../components/ui/Button/Button';
import {
  emailValidator, nameValidator, phoneValidator,
} from '../../../../utils/formUtils';
import S, { DriverDatePickerGlobal } from './AddDriverStyles';
import useDriverThumbnail from '../../../../theme/useDriverThumbnail';
import Tags from '../../../../components/common/Tags';
import { generateLicenseExpiration, generatePreviewFullName, generatePreviewLicense } from '../helpers';
import { FeaturesManagement } from '../../../../featuresControl';
import { DriverSettingsDefault } from '../../../../constants/DriverSettings';

type Props = {
  fleetId: string;
  toggleModal: (closed: boolean) => void;
};

type DriverObj = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  licenseName: string;
  // vehicleClassType: string;
  licenseExpireAt: string;
  file: any,
};

const defaultValue: DriverObj = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  licenseName: '',
  // vehicleClassType: '',
  licenseExpireAt: '',
  file: undefined,
};

function AddDriver({ fleetId, toggleModal }: Props): JSX.Element {
  const driverThumbnail = useDriverThumbnail();
  const licenseExpireAtId = useId();
  const [submitError, setSubmitError] = useState<string | string[] | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | undefined>(undefined);
  const onSubmitSuccess = (): void => toggleModal(false);
  const onSubmitFail = (errorMessage: string): void => setSubmitError(errorMessage);

  // formik can't process async actions and work with server response,
  // thus we have to pass callbacks to tanstack mutation
  const { mutate } = usePostDriver(onSubmitSuccess, onSubmitFail);

  const formik = useFormik({
    initialValues: defaultValue,
    onSubmit: (values) => mutate({
      ...values,
      licenseExpireAt: values.licenseExpireAt?.replace(/(\d+[/])(\d+[/])/, '$2$1'),
      workingHours: {},
      fleetId,
    } as unknown as PostDriverRequest),
    validate: (values) => {
      const errors = {} as Record<string, string | undefined>;

      if (!values.firstName.length) errors.firstName = 'Required';
      if (values.firstName.length && !nameValidator.test(values.firstName)) {
        errors.firstName = 'Can contain only letters';
      }
      if (!values.lastName.length) errors.lastName = 'Required';
      if (values.lastName.length && !nameValidator.test(values.lastName)) {
        errors.lastName = 'Can contain only letters';
      }

      if (!values.email.length) errors.email = 'Required';
      if (values.email.length && !emailValidator.test(values.email)) {
        errors.email = 'Invalid email';
      }

      if (!values.phoneNumber.length) errors.phoneNumber = 'Required';
      if (values.phoneNumber.length && !phoneValidator.test(values.phoneNumber)) {
        errors.phoneNumber = 'Invalid phone number';
      }
      if (!values.licenseName.length) errors.licenseName = 'Required';
      // if (!values.vehicleClassType.length) errors.vehicleClassType = 'Required';
      if (!values.licenseExpireAt.length) errors.licenseExpireAt = 'Required';
      if (values.file && (values.file.size > DriverSettingsDefault.avatarMaxSize)) {
        errors.file = `Maximum size 250KB is allowed (actually ${Math.round(values.file.size / 1024)} KB)`;
      }
      return errors;
    },
    validateOnMount: true,
  });

  // if there is onSubmit error formik will stack in submitting state
  // blocking further interactions with submit button
  useEffect(() => {
    formik.setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitError]);

  const handleInputChange = (e: ChangeEvent<any>): void => {
    formik.handleChange(e);
    setSubmitError('');
  };

  const handleAvatarChange = (e: ChangeEvent<any>): void => {
    formik.handleChange(e);
    const file = e.target.files[0];
    if (file) {
      formik.errors.file = undefined;
      const reader = new FileReader();
      reader.onload = function (event) {
        const imageDataURL = event?.target?.result;
        setAvatarPreview(imageDataURL as string);
        formik.setFieldValue('file', file);
      };
      reader.readAsDataURL(file);
    } else {
      formik.setFieldValue('file', undefined);
    }
  };

  return (
    <>
      <DriverDatePickerGlobal />
      <S.Wrapper>
        <form onSubmit={formik.handleSubmit}>
          <div className="details-container">
            <h4 className="form-title"> Personal details </h4>
            <S.RowContainer>
              <Input
                label="First name"
                name="firstName"
                placeholder="George"
                onChange={handleInputChange}
                onBlur={formik.handleBlur}
                value={formik.values.firstName}
                error={formik.touched.firstName && formik.errors.firstName}
              />
              <Input
                label="Surname"
                name="lastName"
                placeholder="Lopez"
                onChange={handleInputChange}
                onBlur={formik.handleBlur}
                value={formik.values.lastName}
                error={formik.touched.lastName && formik.errors.lastName}
              />
            </S.RowContainer>
            <S.RowContainer>
              <Input
                label="Email"
                name="email"
                placeholder="yourname@email.com"
                type="email"
                onChange={handleInputChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
                error={formik.touched.email && formik.errors.email}
              />
              <Input
                label="Phone number"
                name="phoneNumber"
                placeholder="0124447778888"
                type="tel"
                maxLength={13}
                onChange={handleInputChange}
                onBlur={formik.handleBlur}
                value={formik.values.phoneNumber}
                error={formik.touched.phoneNumber && formik.errors.phoneNumber}
              />
            </S.RowContainer>
            <S.RowContainer>
              <Input
                label="License"
                name="licenseName"
                onChange={handleInputChange}
                onBlur={formik.handleBlur}
                value={formik.values.licenseName}
                error={formik.touched.licenseName && formik.errors.licenseName}
              />
              <S.DatePickerWrapper error={!!(formik.touched.licenseExpireAt && formik.errors.licenseExpireAt)}>
                <label htmlFor={licenseExpireAtId} className="date-label">License expiration date</label>
                <ReactDatePicker
                  id={licenseExpireAtId}
                  selected={formik.values.licenseExpireAt ? new Date(formik.values.licenseExpireAt.split('/').reverse().join('-')) : null}
                  onChange={(date: Date | null) => {
                    const value = date ? date.toLocaleDateString('en-GB') : '';
                    formik.setFieldValue('licenseExpireAt', value, true).then(() => {
                      formik.setFieldTouched('licenseExpireAt', true, false);
                    });
                    setSubmitError('');
                  }}
                  onBlur={formik.handleBlur}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="dd/mm/yyyy"
                  showPopperArrow={false}
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="scroll"
                  yearDropdownItemNumber={20}
                  popperClassName="driver-license-datepicker-popper"
                  className="date-input"
                  wrapperClassName="date-input-wrapper"
                />
                {formik.touched.licenseExpireAt && formik.errors.licenseExpireAt && (
                  <span className="date-error">{formik.errors.licenseExpireAt}</span>
                )}
              </S.DatePickerWrapper>
            </S.RowContainer>
            {FeaturesManagement.setting.avatarUploading && (
              <S.RowContainer>
                <Input
                  label="Avatar"
                  type="file"
                  name="file"
                  placeholder="Select photo"
                  onChange={handleAvatarChange}
                  accept="image/png, image/jpeg"
                  error={formik.touched.file && formik.errors.file as string}
                />
              </S.RowContainer>
            )}
            {!!submitError && (
              typeof submitError === 'string'
                ? <S.ErrorMessage>{submitError}</S.ErrorMessage>
                : submitError.map((error) => <S.ErrorMessage>{error}</S.ErrorMessage>)
            )}
          </div>
          <div className="preview-container">
            <div className="preview-card">
              <h4 className="preview-card-title"> Driver preview </h4>
              <div className="preview-card-tags-container">
                <img src={avatarPreview || driverThumbnail} alt="driver" />
                <p className="name">
                  {generatePreviewFullName(formik.values.firstName, formik.values.lastName)}
                </p>
                <p className="license"> {generatePreviewLicense(formik.values.licenseName)} </p>
                {/* <Tags.LabeledIcon label="Add license details" icon={IconKeys.CAR_HATCHBACK} /> */}
                {/* <Tags.LabeledIcon label="Add schedule" icon={IconKeys.CLOCK} /> */}
                <Tags.LabeledIcon
                  label={generateLicenseExpiration(formik.values.licenseExpireAt)}
                  icon={IconKeys.CLIPBOARD_PLUS}
                />
              </div>
              <div className="preview-card-buttons-container">
                <Button
                  height="50px"
                  onClick={() => toggleModal(false)}
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  className="add-button"
                  height="50px"
                  type="submit"
                  disabled={formik.isSubmitting || !formik.isValid || !!submitError}
                >
                  Add driver
                </Button>
              </div>
            </div>
          </div>
        </form>
      </S.Wrapper>
    </>
  );
}

export default AddDriver;
